import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Kpi, EmptyState, LiveBadge } from "@/components/common";
import { OrderRow } from "@/components/orders/OrderRow";
import { fmtUSDStrict } from "@/lib/format";
import { Clock, Radio, CheckCircle2, Lock, RefreshCw, ListChecks } from "lucide-react";

export default function Orders() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (spin = false) => {
    if (spin) setBusy(true);
    try {
      const { data: res } = await api.get("/orders/status");
      setData(res);
    } catch (e) {
      console.error("Failed to load order status:", e);   // keep the last good snapshot on screen
    }
    finally { if (spin) setBusy(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load]);

  if (!data) return <AppShell><div className="p-8 text-sm text-muted-foreground">Loading orders...</div></AppShell>;

  return (
    <AppShell>
    <div data-testid="orders-page">
      <PageHeader title="Orders"
        subtitle="Everything working in the market right now, filled slice by slice, with the broker events behind each one."
        testid="orders-header"
        action={
          <div className="flex items-center gap-2">
            <LiveBadge />
            <Button variant="outline" size="sm" onClick={() => load(true)} disabled={busy}
              data-testid="orders-refresh">
              <RefreshCw className={`h-4 w-4 me-1.5 ${busy ? "animate-spin" : ""}`} />Refresh
            </Button>
          </div>
        } />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Kpi label="Working" value={data.counts.pending} icon={Clock} tint={0} testid="orders-kpi-pending" />
        <Kpi label="Partially filled" value={data.counts.partial} icon={Radio} tint={1} testid="orders-kpi-partial" />
        <Kpi label="Filled" value={data.counts.filled} icon={CheckCircle2} tint={2} testid="orders-kpi-filled" />
        <Kpi label="Cash held" value={fmtUSDStrict(data.held_usd)} icon={Lock} tint={3} testid="orders-kpi-held" />
      </div>

      <Card className="mt-4 sm:mt-5 shadow-premium overflow-hidden" data-testid="orders-open-card">
        <div className="px-3.5 py-3 border-b flex items-center justify-between gap-3">
          <div className="text-sm font-semibold">Working orders</div>
          <div className="text-[11px] text-muted-foreground">Updated every 10 seconds</div>
        </div>
        {data.open.length ? data.open.map((o) => <OrderRow key={o.id} order={o} onChanged={() => load(true)} />)
          : <EmptyState title="Nothing working right now"
              hint="Place a limit order away from the market and it will wait here until the price comes to you."
              testid="orders-open-empty" />}
      </Card>

      <p className="text-[11px] text-muted-foreground mt-2.5" data-testid="orders-venue-note">{data.note}</p>

      <Card className="mt-4 sm:mt-5 shadow-premium overflow-hidden" data-testid="orders-recent-card">
        <div className="px-3.5 py-3 border-b text-sm font-semibold">Recent orders</div>
        {data.recent.length ? data.recent.map((o) => <OrderRow key={o.id} order={o} />)
          : <EmptyState title="No orders yet"
              hint="Your fills, partial fills and cancellations will all be listed here."
              testid="orders-recent-empty" />}
      </Card>
    </div>
    </AppShell>
  );
}
